import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Table, TableHead, TableRow, TableCell,
  TableBody, Chip, CircularProgress, Alert, IconButton, Tooltip,
  Switch, FormControlLabel, MenuItem, Select, FormControl, InputLabel,
  TableContainer,
} from '@mui/material';
import { Add, Edit, Delete, LocalOffer, Refresh } from '@mui/icons-material';
import { fetchDiscounts, createDiscount, updateDiscount, deleteDiscount } from '../../api/api';

const KES = (n) => `KES ${Number(n || 0).toLocaleString('en-KE', { minimumFractionDigits: 2 })}`;
const fmt = (d) => d ? new Date(d).toLocaleDateString() : '—';

const EMPTY_FORM = {
  name: '',
  code: '',
  type: 'percentage',
  value: '',
  minOrderAmount: '',
  maxUses: '',
  startDate: '',
  endDate: '',
  active: true,
};

function discountStatus(d) {
  if (!d.active) return { label: 'Inactive', color: 'default' };
  const now = new Date();
  if (d.endDate && new Date(d.endDate) < now) return { label: 'Expired', color: 'error' };
  if (d.startDate && new Date(d.startDate) > now) return { label: 'Scheduled', color: 'info' };
  return { label: 'Active', color: 'success' };
}

export default function ManageDiscounts() {
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  /* dialog */
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState('');

  /* delete confirm */
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => { loadDiscounts(); }, []);

  const loadDiscounts = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await fetchDiscounts();
      setDiscounts(res.data?.discounts || res.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load discounts.');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditTarget(null);
    setForm(EMPTY_FORM);
    setSaveError('');
    setDialogOpen(true);
  };

  const openEdit = (d) => {
    setEditTarget(d);
    setForm({
      name: d.name || '',
      code: d.code || '',
      type: d.type || 'percentage',
      value: d.value ?? '',
      minOrderAmount: d.minOrderAmount ?? '',
      maxUses: d.maxUses ?? '',
      startDate: d.startDate ? d.startDate.slice(0, 10) : '',
      endDate: d.endDate ? d.endDate.slice(0, 10) : '',
      active: d.active !== false,
    });
    setSaveError('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setSaveError('Name is required.'); return; }
    if (!form.value || Number(form.value) <= 0) { setSaveError('Enter a valid discount value.'); return; }
    if (form.type === 'percentage' && Number(form.value) > 100) { setSaveError('Percentage cannot exceed 100%.'); return; }

    const payload = {
      ...form,
      code: form.code.trim().toUpperCase() || undefined,
      value: Number(form.value),
      minOrderAmount: form.minOrderAmount ? Number(form.minOrderAmount) : 0,
      maxUses: form.maxUses ? Number(form.maxUses) : null,
      startDate: form.startDate || null,
      endDate: form.endDate || null,
    };

    try {
      setSaveLoading(true);
      setSaveError('');
      if (editTarget) {
        await updateDiscount(editTarget._id, payload);
      } else {
        await createDiscount(payload);
      }
      setDialogOpen(false);
      await loadDiscounts();
    } catch (err) {
      setSaveError(err.response?.data?.message || 'Failed to save discount.');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleteLoading(true);
      await deleteDiscount(deleteTarget._id);
      setDeleteTarget(null);
      await loadDiscounts();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete discount.');
    } finally {
      setDeleteLoading(false);
    }
  };

  /* ════════════════ RENDER ════════════════ */
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1e293b">Discounts & Promotions</Typography>
          <Typography variant="body2" color="text.secondary">Manage coupon codes and promotional discounts</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={loadDiscounts} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={openCreate}
            sx={{ borderRadius: 2, background: 'linear-gradient(135deg,#667eea,#764ba2)' }}
          >
            Create Discount
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                {['Name', 'Code', 'Type', 'Value', 'Min Order', 'Uses', 'Status', 'Dates', 'Actions'].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12, color: '#64748b' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {discounts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 6, color: '#94a3b8' }}>
                    No discounts yet. Create your first promotion!
                  </TableCell>
                </TableRow>
              ) : discounts.map((d) => {
                const st = discountStatus(d);
                return (
                  <TableRow key={d._id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <LocalOffer sx={{ fontSize: 15, color: '#667eea' }} />
                        <Typography fontWeight={600} variant="body2">{d.name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {d.code ? (
                        <Chip label={d.code} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontWeight: 700 }} />
                      ) : <Typography variant="caption" color="text.secondary">Auto</Typography>}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={d.type === 'percentage' ? 'Percent' : d.type === 'fixed' ? 'Fixed' : 'BOGO'}
                        size="small"
                        color={d.type === 'percentage' ? 'primary' : d.type === 'fixed' ? 'secondary' : 'warning'}
                        sx={{ fontWeight: 700 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={700} color="primary">
                        {d.type === 'percentage' ? `${d.value}%` : d.type === 'fixed' ? KES(d.value) : 'BOGO'}
                      </Typography>
                    </TableCell>
                    <TableCell>{d.minOrderAmount ? KES(d.minOrderAmount) : 'None'}</TableCell>
                    <TableCell>
                      <Typography variant="caption">{d.usedCount ?? 0}{d.maxUses ? ` / ${d.maxUses}` : ''}</Typography>
                    </TableCell>
                    <TableCell><Chip label={st.label} color={st.color} size="small" sx={{ fontWeight: 700 }} /></TableCell>
                    <TableCell>
                      <Typography variant="caption" display="block">{d.startDate ? fmt(d.startDate) : 'Anytime'}</Typography>
                      <Typography variant="caption" color="text.secondary">{d.endDate ? `to ${fmt(d.endDate)}` : 'No expiry'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(d)}>
                            <Edit fontSize="small" color="primary" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => setDeleteTarget(d)}>
                            <Delete fontSize="small" color="error" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ─── Create / Edit Dialog ─── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle fontWeight={800}>{editTarget ? 'Edit Discount' : 'Create Discount'}</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth size="small" />
            <TextField
              label="Coupon Code (optional)"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              fullWidth
              size="small"
              inputProps={{ style: { textTransform: 'uppercase', fontFamily: 'monospace' } }}
              helperText="Leave blank if no code required"
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Discount Type</InputLabel>
              <Select value={form.type} label="Discount Type" onChange={(e) => setForm({ ...form, type: e.target.value })}>
                <MenuItem value="percentage">Percentage (%)</MenuItem>
                <MenuItem value="fixed">Fixed Amount (KES)</MenuItem>
                <MenuItem value="bogo">BOGO (Buy One Get One)</MenuItem>
              </Select>
            </FormControl>
            {form.type !== 'bogo' && (
              <TextField
                label={form.type === 'percentage' ? 'Percentage (%)' : 'Amount (KES)'}
                type="number"
                value={form.value}
                onChange={(e) => setForm({ ...form, value: e.target.value })}
                fullWidth
                size="small"
                inputProps={{ min: 0, max: form.type === 'percentage' ? 100 : undefined }}
              />
            )}
            <TextField
              label="Minimum Order Amount (KES)"
              type="number"
              value={form.minOrderAmount}
              onChange={(e) => setForm({ ...form, minOrderAmount: e.target.value })}
              fullWidth
              size="small"
              helperText="0 for no minimum"
            />
            <TextField
              label="Max Uses (leave blank for unlimited)"
              type="number"
              value={form.maxUses}
              onChange={(e) => setForm({ ...form, maxUses: e.target.value })}
              fullWidth
              size="small"
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                type="date"
                label="Start Date"
                value={form.startDate}
                onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
              <TextField
                type="date"
                label="End Date"
                value={form.endDate}
                onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
            </Box>
            <FormControlLabel
              control={<Switch checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} color="primary" />}
              label="Active"
            />
            {saveError && <Alert severity="error">{saveError}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saveLoading}
            sx={{ borderRadius: 2, background: 'linear-gradient(135deg,#667eea,#764ba2)' }}>
            {saveLoading ? <CircularProgress size={18} /> : editTarget ? 'Save Changes' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Delete Confirm ─── */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle fontWeight={800} color="error">Delete Discount?</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete <strong>{deleteTarget?.name}</strong>? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleteLoading} sx={{ borderRadius: 2 }}>
            {deleteLoading ? <CircularProgress size={18} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
